import useInterval from 'hooks/useInterval';
import {useCallback, useRef, useState} from 'react';
import styled from 'styled-components';
import ActionButton from 'screens/commons/ActionButton';
import {useMediaQuery} from 'hooks/useMediaQuery';

export const ProgressBar = ({
  waitTimeInSec = 100,
  loadingMsgIntervalInSec = null,
  loadingTexts = [
    'Loading...',
    'Please wait...',
    'Almost there...',
    'Fetching data...',
    'Just a moment...',
    'It is taking longer than expected...',
  ],
  stopOnEndTime = false,
  stopOnEndMsg = null,
  subheading = 'Please wait while we fetch your kyc details',
  heading = 'Processing to next step',
  footerTxt = '',
  showShadow = true,
  noBorder = false,
}) => {
  const [currentValue, setCurrentValue] = useState(0);
  const [loadingText, setLoadingText] = useState(loadingTexts[0]);
  const indexRef = useRef(0);
  const maxValue = 100;
  const eachIncrementValue = 100 / waitTimeInSec;
  const textChangeInterval =
    loadingMsgIntervalInSec || (waitTimeInSec * 1000) / loadingTexts.length;
  const isMobile = useMediaQuery('(max-width:768px)');

  const increment = useCallback(() => {
    if (currentValue + eachIncrementValue >= 100) {
      if (!stopOnEndTime) {
        setCurrentValue(0);
      } else {
        setCurrentValue(100);
      }
    } else {
      setCurrentValue((v) => v + eachIncrementValue);
    }
  }, [setCurrentValue, currentValue]);

  const setText = () => {
    if (currentValue >= 100 && stopOnEndTime) {
      stopOnEndMsg && setLoadingText(stopOnEndMsg);
      return;
    }
    setLoadingText(loadingTexts[indexRef.current]);
    indexRef.current = (indexRef.current + 1) % loadingTexts.length;
  };

  const handleRetry = () => {
    window.location.reload();
  };

  useInterval(increment, 1000);
  useInterval(setText, textChangeInterval);

  const showRetryButton = currentValue >= 100 && stopOnEndTime;

  return (
    <BarContainer>
      <BarCard className="bar-card" showShadow={showShadow && !isMobile} noBorder={noBorder}>
        {(heading || subheading) && (
          <BarHeader>
            {heading && <BarHeading>{heading}</BarHeading>}
            {subheading && <BarSubheading>{subheading}</BarSubheading>}
          </BarHeader>
        )}
        <BarTrack>
          <BarFill style={{width: `${currentValue}%`}} />
        </BarTrack>
        <BarPercent>{Math.floor(currentValue)}%</BarPercent>
        <BarLoadingText>{loadingText}</BarLoadingText>
        {showRetryButton ? (
          <ActionButton
            buttonText="Retry"
            callback={handleRetry}
            additionalStyles={{marginTop: '1rem', marginBottom: '1rem'}}
          />
        ) : null}
        {footerTxt && <BarFooter>{footerTxt}</BarFooter>}
        {showRetryButton ? null : <WarningText>Please do not close this window</WarningText>}
      </BarCard>
    </BarContainer>
  );
};

const WarningText = styled.p`
  text-align: center;
  font-size: 0.75rem;
  color: #a0aec0;
  margin-top: 1rem;
  margin-bottom: 0;
`;

const BarContainer = styled.div`
  width: 100%;
  min-height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
`;

const BarCard = styled.div`
  max-width: 580px;
  min-width: 320px;
  display: flex;
  flex-direction: column;
  align-items: center;
  border: ${(props) =>
    props.noBorder ? 'none' : `1px solid ${props.theme.colors.border.default}`};
  padding: 2rem;
  border-radius: ${({theme}) => theme.borderRadius?.md || '0.75rem'};

  @media screen and (min-width: 768px) {
    background: ${({theme}) => theme.colors.background.primary};
    box-shadow: ${(props) => (props.showShadow ? '0 4px 12px rgba(0,0,0,0.08)' : 'none')};
    min-width: 380px;
  }
`;

const BarHeader = styled.div`
  width: 100%;
  text-align: center;
  margin-bottom: 1.25rem;
`;

const BarHeading = styled.p`
  font-size: ${({theme}) => theme.typography.fontSize.md || '1rem'};
  font-weight: ${({theme}) => theme.typography.fontWeight.regular};
  color: ${({theme}) => theme.colors.text.primary};
  margin: 0 0 0.25rem 0;
`;

const BarSubheading = styled.p`
  font-size: ${({theme}) => theme.typography.fontSize.xs};
  color: ${({theme}) => theme.colors.text.secondary};
  margin: 0 0 0.5rem 0;
  color: #718096;
`;

const BarPercent = styled.div`
  font-size: ${({theme}) => theme.typography.fontSize.xs || '1rem'};
  font-weight: ${({theme}) => theme.typography.fontWeight.semiBold || 600};
  color: ${({theme}) => theme.colors.brand.primary};
  margin-bottom: 1.25rem;
  opacity: 0.9;
`;

const BarTrack = styled.div`
  width: 100%;
  max-width: 220px;
  height: 12px;
  background: ${({theme}) => theme.colors.background.secondary || '#f3f4f6'};
  border-radius: 9999px;
  overflow: hidden;
  margin-bottom: 1.25rem;
`;

const BarFill = styled.div`
  height: 100%;
  background: ${({theme}) => theme.colors.brand.primary};
  border-radius: 9999px;
  transition: width 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  opacity: 0.9;
`;

const BarLoadingText = styled.div`
  text-align: center;
  font-size: ${({theme}) => theme.typography.fontSize.xs || '0.8rem'};
  color: ${({theme}) => theme.colors.brand.primary};
  min-height: 2.5em;
  margin-top: 0.5rem;
  opacity: 0.9;
`;

const BarFooter = styled.div`
  text-align: center;
  font-size: ${({theme}) => theme.typography.fontSize.sm || '0.875rem'};
  color: ${({theme}) => theme.colors.text.secondary};
  margin-top: 1.5rem;
`;
